import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { kitText } from "../../styles";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { css } from "@emotion/react";
import { MarkdownProps } from "./types";
import { styles } from "./styles";

/**
 * Renders Markdown content to HTML.
 * #### Supports:
 * - GitHub Flavored Markdown (GFM).
 * - Sanitization of HTML to prevent XSS attacks.
 * - Custom components for Markdown elements (e.g. h1, p, etc.).
 */
function Markdown({
  children,
  components,
  disallowedElements,
  disallowRawHtml
}: MarkdownProps) {
  const mdComponents = useMemo(
    () => getMarkdownComponents(components),
    [components]
  );

  const rehypePlugins = useMemo(() => {
    if (disallowRawHtml) {
      return [];
    }
    return [rehypeRaw, rehypeSanitize];
  }, [disallowRawHtml]);

  if (!children) {
    return null;
  }

  return (
    <div className="kit-Markdown" css={[styles.base]}>
      <ReactMarkdown
        components={mdComponents}
        disallowedElements={disallowedElements}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Default components for rendering Markdown elements.
 * These can be overridden by defining {@link MarkdownProps.components}
 * when using the Markdown component.
 */
const DEFAULT_MARKDOWN_COMPONENTS: MarkdownProps["components"] = {
  h1: ({ children, ...props }) => (
    <h1 css={[kitText.variant.heading1]} {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 css={[kitText.variant.heading2]} {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 css={[kitText.variant.heading3]} {...props}>
      {children}
    </h3>
  ),
  p: ({ children, ...props }) => (
    <p
      css={[
        kitText.variant.body,
        css`
          margin-bottom: 0.25rem;
        `
      ]}
      {...props}
    >
      {children}
    </p>
  )
};

/**
 * Merges the default Markdown components
 * with any custom components provided via props.
 */
function getMarkdownComponents(components: MarkdownProps["components"] = {}) {
  return { ...DEFAULT_MARKDOWN_COMPONENTS, ...components };
}

export default Markdown;
export * from "./types";
